import { z } from "zod";

import { CalendarDateSchema, MONEY_LIMIT, hasAtMostTwoDecimals } from "./common.js";

/**
 * DTOs for the properties context.
 *
 * Same contract role as `identity.ts` and `savings.ts`: use cases return these, route handlers pass
 * them through unchanged, and `@gladis/api-client` derives its types from the same schemas — so a
 * contract change is a compile error on both sides at once.
 *
 * THREE REPRESENTATION DECISIONS ARE ENCODED HERE.
 *
 * **Money crosses as a number of EUROS.** `12.5` is twelve euros fifty; never cents, never a string.
 * A single stored amount is exactly representable, and what is not safe — adding them up — happens
 * in `Valuation` (integer cents) before it reaches this edge.
 *
 * **Dates cross as `YYYY-MM-DD`.** `purchaseDate`, `valuationDate`, `startDate` and `saleDate` are
 * all `date` columns. `recordedAt`, `archivedAt` and `createdAt` are real instants and stay ISO
 * timestamps. See `common.ts` for why the distinction is load-bearing here in a way it was not
 * before this feature.
 *
 * **The energy label crosses as its DISPLAY form.** `"A++"`, not `A_plus_plus`. The Postgres enum
 * cannot hold `+` in a bare label, so the storage form is an encoding detail and it stops at the
 * adapter — the same argument `formatEur` makes for being the one place an amount becomes a string.
 *
 * NOTHING HERE ACCEPTS AN OUTSTANDING BALANCE. No request body has a field for it, because nobody
 * types in what they still owe (spec 008 FR-028, SC-004). The balance and the monthly interest
 * appear on RESPONSES only, derived at read time.
 */

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

export const PropertyTypeSchema = z.enum(["house", "apartment"]);
export type PropertyTypeValue = z.infer<typeof PropertyTypeSchema>;

/**
 * The display form, in the household's own vocabulary.
 *
 * The column's values are `A_plus_plus` … `G`, because a Postgres enum label cannot contain `+`.
 * Translation lives in one place in the domain and never leaks past the adapter.
 */
export const EnergyLabelSchema = z.enum(["A++", "A+", "A", "B", "C", "D", "E", "F", "G"]);
export type EnergyLabelValue = z.infer<typeof EnergyLabelSchema>;

export const MortgageTypeSchema = z.enum(["annuity", "linear", "interest_only"]);
export type MortgageTypeValue = z.infer<typeof MortgageTypeSchema>;

/**
 * `woz` and `avm` exist in the column and are never written by this feature.
 *
 * Every valuation feature 008 records is `manual`. Feature 011 adds `avm` rows beside them — rows,
 * not a reshaping, which is the whole reason the source is recorded on every entry from the start.
 */
export const ValuationSourceSchema = z.enum(["manual", "woz", "index", "avm"]);
export type ValuationSourceValue = z.infer<typeof ValuationSourceSchema>;

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/**
 * An amount that must be positive: a purchase price, a valuation, an original principal.
 *
 * Stated here as well as in the domain because the schema is the outer edge: rejecting at the
 * boundary means the domain's own refusal (`PROP_AMOUNT_OUT_OF_RANGE`) is a backstop rather than the
 * only guard, and nothing capable of failing AT the database is ever sent to it.
 */
export const PositiveAmountSchema = z
  .number()
  .finite()
  .positive()
  .max(MONEY_LIMIT)
  .refine(hasAtMostTwoDecimals, { message: "At most two decimal places." });

/**
 * A derived amount that may be negative: equity, gain.
 *
 * Negative equity is a real situation — an underwater mortgage — and spec 008 FR-036 requires it to
 * be shown rather than clamped, hidden, or treated as an error.
 */
export const SignedAmountSchema = z
  .number()
  .finite()
  .min(-MONEY_LIMIT)
  .max(MONEY_LIMIT)
  .refine(hasAtMostTwoDecimals, { message: "At most two decimal places." });

/**
 * An annual interest rate as a percentage. `3.5` is three and a half percent.
 *
 * `numeric(6,3)` and `>= 0`, both the column's own. **Zero is valid**, and it is the case the
 * annuity formula divides by zero on if its degenerate branch is missing — see the amortisation
 * service. A schema that refused zero would hide that rather than fix it.
 */
export const InterestRateSchema = z
  .number()
  .finite()
  .min(0)
  .max(999.999)
  .refine((value) => {
    const text = String(value);
    if (text.includes("e") || text.includes("E")) return false;
    const point = text.indexOf(".");
    return point === -1 || text.length - point - 1 <= 3;
  }, "At most three decimal places.");

/** A term in whole months. 360 is a thirty-year mortgage. */
export const DurationMonthsSchema = z.number().int().positive().max(1200);

/**
 * The year a property was built.
 *
 * The upper bound is resolved when the value is checked rather than when this module loads, so a
 * long-running process does not refuse next year's new build on 1 January.
 */
export const BuildYearSchema = z
  .number()
  .int()
  .min(1500)
  .refine((year) => year <= new Date().getUTCFullYear(), "That year is in the future.");

export const AddressSchema = z.object({
  street: z.string().trim().min(1).max(200),
  houseNumber: z.string().trim().min(1).max(20),
  /**
   * Optional AND nullable, and the distinction cost an afternoon.
   *
   * `.nullable()` alone requires the key to be PRESENT with an explicit `null` — so a client that
   * simply omits an addition, which is the common case, gets a 400. The failure is not where you
   * look for it either: the create returns 400, the caller carries on with an undefined id, and the
   * next request sends `undefined` into a `uuid` parameter, where it surfaces as a driver-level
   * `UNDEFINED_VALUE` and a 500 from a completely different endpoint.
   */
  houseAddition: z.string().trim().max(20).nullable().optional(),
  postcode: z.string().trim().min(1).max(20),
  city: z.string().trim().min(1).max(120),
  /** ISO 3166-1 alpha-2, upper case. The column checks the length; this checks the shape. */
  countryCode: z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, "Expected a two-letter country code, upper case."),
});
export type AddressValue = z.infer<typeof AddressSchema>;

/** Square metres. Optional throughout — a household may simply not know. */
const SurfaceAreaSchema = z.number().finite().positive().max(1_000_000).nullable();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * One row of the Properties page.
 *
 * `totalOwed`, `equity`, `gain` and `gainPct` are all DERIVED at read time and stored nowhere. The
 * household totals above the list are the sums of these already-rounded figures, so a household that
 * adds up the column gets the total (spec 008 NFR-003).
 */
export const PropertyDtoSchema = z.object({
  id: z.uuid(),
  address: AddressSchema,
  propertyType: PropertyTypeSchema,
  purchasePrice: PositiveAmountSchema,
  purchaseDate: CalendarDateSchema,
  /** The latest-dated valuation. Never absent for an active property — FR-008 guarantees it. */
  currentValue: PositiveAmountSchema,
  currentValueDate: CalendarDateSchema,
  /** Sum of the non-archived parts' balances, each rounded before it was added. */
  totalOwed: PositiveAmountSchema.or(z.literal(0)),
  equity: SignedAmountSchema,
  /** Value gain only. Mortgage paydown is deliberately excluded — spec 008 FR-037. */
  gain: SignedAmountSchema,
  gainPct: z.number().finite(),
  archivedAt: z.iso.datetime().nullable(),
});
export type PropertyDto = z.infer<typeof PropertyDtoSchema>;

/** The descriptive fields the list does not carry. */
export const PropertyDetailFieldsSchema = z.object({
  buildYear: z.number().int(),
  innerSurfaceAreaM2: SurfaceAreaSchema,
  outerSurfaceAreaM2: SurfaceAreaSchema,
  energyLabel: EnergyLabelSchema.nullable(),
  latitude: z.number().finite().min(-90).max(90).nullable(),
  longitude: z.number().finite().min(-180).max(180).nullable(),

  /**
   * Whether the "Update estimate" control should be offered — 017, FR-P3/AC-PR3.
   *
   * True for a Dutch property whose address resolved to a COROP region. The control is hidden rather
   * than disabled for the rest: a disabled button invites a member to work out why, and for a French
   * property the answer is "never", which is not a state worth rendering.
   *
   * DERIVED ON READ, not stored. It is two facts already on the row.
   */
  indexable: z.boolean(),

  /**
   * The date of the newest member-entered valuation, when the current value is an estimate — 017,
   * FR-031. `null` when the current value is itself real, so the page shows nothing on the day
   * somebody enters one.
   *
   * This is the honesty half of the indexed valuation. The failure mode of an estimate is not a
   * wrong number but a number that looks maintained while drifting years from anything real, and
   * this field is the only thing standing between the two.
   */
  estimatedSince: CalendarDateSchema.nullable(),
});

export const PropertyValuationDtoSchema = z.object({
  id: z.uuid(),
  value: PositiveAmountSchema,
  valuationDate: CalendarDateSchema,
  source: ValuationSourceSchema,
  notes: z.string().nullable(),
  /** A real instant, and the tie-break when two entries share a date. */
  recordedAt: z.iso.datetime(),
});
export type PropertyValuationDto = z.infer<typeof PropertyValuationDtoSchema>;

/**
 * One `leningdeel`, with everything about it that is derived.
 *
 * `outstandingBalance` and `monthlyInterest` are computed from the six parameters and the evaluation
 * date on every read — there is no column for either. `paymentsMade` is returned so a household can
 * check the arithmetic against their own amortisation table rather than having to trust it.
 */
export const MortgagePartDtoSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  mortgageType: MortgageTypeSchema,
  originalPrincipal: PositiveAmountSchema,
  interestRate: InterestRateSchema,
  startDate: CalendarDateSchema,
  durationMonths: DurationMonthsSchema,
  lender: z.string().nullable(),
  outstandingBalance: PositiveAmountSchema.or(z.literal(0)),
  monthlyInterest: PositiveAmountSchema.or(z.literal(0)),
  paymentsMade: z.number().int(),
  archivedAt: z.iso.datetime().nullable(),
});
export type MortgagePartDto = z.infer<typeof MortgagePartDtoSchema>;

export const PropertyTotalsDtoSchema = z.object({
  currentValue: SignedAmountSchema,
  totalOwed: SignedAmountSchema,
  equity: SignedAmountSchema,
});

/**
 * `GET /api/properties`.
 *
 * `asOf` is the ONE evaluation date the whole response was computed against (FR-030). Two figures in
 * one response can never straddle midnight, and returning the date makes that checkable rather than
 * merely intended.
 *
 * `archived` is always present, in the same response as `active`, for the reason savings returns its
 * archived accounts the same way: revealing them is a disclosure, not a filter, and the totals must
 * not change when the section is expanded.
 */
export const PropertyListDtoSchema = z.object({
  asOf: CalendarDateSchema,
  totals: PropertyTotalsDtoSchema,
  active: z.array(PropertyDtoSchema),
  archived: z.array(PropertyDtoSchema),
});
export type PropertyListDto = z.infer<typeof PropertyListDtoSchema>;

/** `GET /api/properties/:propertyId`. Parts come back in BOTH states; the client filters. */
export const PropertyDetailDtoSchema = z.object({
  asOf: CalendarDateSchema,
  property: PropertyDtoSchema.merge(PropertyDetailFieldsSchema),
  valuations: z.array(PropertyValuationDtoSchema),
  mortgageParts: z.array(MortgagePartDtoSchema),
});
export type PropertyDetailDto = z.infer<typeof PropertyDetailDtoSchema>;

/**
 * `GET /api/properties/mortgage-interest` — the figure feature 009 consumes.
 *
 * One line per non-archived, STARTED part; the amount is zero once the part is past its duration.
 * Archived and not-yet-started parts produce no line at all (spec 008 FR-040).
 *
 * There is no category on this DTO and no `Housing` tag. That vocabulary belongs to Expenses, and
 * inventing it here would be this feature deciding something 009 owns.
 */
export const MortgageInterestLineDtoSchema = z.object({
  propertyId: z.uuid(),
  propertyLabel: z.string(),
  mortgagePartId: z.uuid(),
  mortgagePartName: z.string().nullable(),
  monthlyInterest: PositiveAmountSchema.or(z.literal(0)),
});

export const MortgageInterestDtoSchema = z.object({
  asOf: CalendarDateSchema,
  lines: z.array(MortgageInterestLineDtoSchema),
});
export type MortgageInterestDto = z.infer<typeof MortgageInterestDtoSchema>;

/** What `archive` and `sell` report back, so the client can say what went with it. */
export const CascadeResultDtoSchema = z.object({
  archivedPartIds: z.array(z.uuid()),
});

/** What `unarchive` reports back — exactly the parts the cascade took, and no others. */
export const RestoreResultDtoSchema = z.object({
  restoredPartIds: z.array(z.uuid()),
});

/** Both halves of a refinance, so the link is visible without a second request. */
export const RefinanceResultDtoSchema = z.object({
  archived: MortgagePartDtoSchema,
  created: MortgagePartDtoSchema,
});

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * The opening valuation is REQUIRED and has no server-side default.
 *
 * The form pre-fills it from the purchase price and purchase date and lets the member change it
 * (FR-009). Defaulting it here instead would mean a client that omitted the field by accident
 * silently got a valuation it never showed anyone.
 */
export const OpeningValuationSchema = z.object({
  value: PositiveAmountSchema,
  valuationDate: CalendarDateSchema,
});

export const CreatePropertyRequestSchema = z.object({
  address: AddressSchema,
  propertyType: PropertyTypeSchema,
  buildYear: BuildYearSchema,
  purchasePrice: PositiveAmountSchema,
  purchaseDate: CalendarDateSchema,
  innerSurfaceAreaM2: SurfaceAreaSchema.optional(),
  outerSurfaceAreaM2: SurfaceAreaSchema.optional(),
  /**
   * Optional at the wire edge, required by the DOMAIN when the country is NL.
   *
   * The rule is conditional on another field, and expressing it here would duplicate a domain
   * invariant in a place that cannot produce `PROP_ENERGY_LABEL_REQUIRED` — so the schema checks the
   * shape and the domain checks the rule.
   */
  energyLabel: EnergyLabelSchema.nullable().optional(),
  latitude: z.number().finite().min(-90).max(90).nullable().optional(),
  longitude: z.number().finite().min(-180).max(180).nullable().optional(),
  openingValuation: OpeningValuationSchema,
});
export type CreatePropertyRequest = z.infer<typeof CreatePropertyRequestSchema>;

/**
 * Descriptive fields only.
 *
 * No monetary field and no archive field, deliberately: a change in value is a valuation, and
 * archiving has its own verb. Neither is something an edit should be able to do by accident.
 */
export const UpdatePropertyRequestSchema = z.object({
  address: AddressSchema,
  propertyType: PropertyTypeSchema,
  buildYear: BuildYearSchema,
  innerSurfaceAreaM2: SurfaceAreaSchema.optional(),
  outerSurfaceAreaM2: SurfaceAreaSchema.optional(),
  energyLabel: EnergyLabelSchema.nullable().optional(),
  latitude: z.number().finite().min(-90).max(90).nullable().optional(),
  longitude: z.number().finite().min(-180).max(180).nullable().optional(),
});
export type UpdatePropertyRequest = z.infer<typeof UpdatePropertyRequestSchema>;

export const AddValuationRequestSchema = z.object({
  value: PositiveAmountSchema,
  valuationDate: CalendarDateSchema,
});
export type AddValuationRequest = z.infer<typeof AddValuationRequestSchema>;

export const MortgagePartInputSchema = z.object({
  name: z.string().trim().max(120).nullable().optional(),
  mortgageType: MortgageTypeSchema,
  originalPrincipal: PositiveAmountSchema,
  interestRate: InterestRateSchema,
  startDate: CalendarDateSchema,
  durationMonths: DurationMonthsSchema,
  lender: z.string().trim().max(120).nullable().optional(),
});

export const AddMortgagePartRequestSchema = MortgagePartInputSchema;
export type AddMortgagePartRequest = z.infer<typeof AddMortgagePartRequestSchema>;

export const UpdateMortgagePartRequestSchema = MortgagePartInputSchema;
export type UpdateMortgagePartRequest = z.infer<typeof UpdateMortgagePartRequestSchema>;

/** The parameters of the NEW part. The old one is archived by the same operation. */
export const RefinanceMortgagePartRequestSchema = MortgagePartInputSchema;
export type RefinanceMortgagePartRequest = z.infer<typeof RefinanceMortgagePartRequestSchema>;

/**
 * `saleDate` defaults to today CLIENT-side, not here.
 *
 * A default resolved on the server would be the server's today, which is not necessarily the
 * household's — and the sale date is the date of a real event, not the date the form was submitted.
 */
export const SellPropertyRequestSchema = z.object({
  salePrice: PositiveAmountSchema,
  saleDate: CalendarDateSchema,
});
export type SellPropertyRequest = z.infer<typeof SellPropertyRequestSchema>;

// ---------------------------------------------------------------------------
// The chart
// ---------------------------------------------------------------------------

/** The same three windows the savings chart offers, for the same reason. */
export const PropertiesChartRangeSchema = z.enum(["1m", "3m", "12m"]);
export type PropertiesChartRangeValue = z.infer<typeof PropertiesChartRangeSchema>;

export const ChartPointSchema = z.object({
  /** `YYYY-MM` — the month the figure is as at the end of. */
  at: z.string().regex(/^\d{4}-\d{2}$/, "Expected YYYY-MM."),
  value: SignedAmountSchema,
});

/**
 * `GET /api/properties/chart`.
 *
 * FOUR SERIES OVER THE SAME MONTHS, drawn as LINES rather than a stack. A stack composes — its top
 * is the whole — and these do not compose: equity is value minus owed, so stacking them would draw
 * the same money twice and produce a total that means nothing.
 *
 * `gain` is the value line shifted down by what was paid. Same shape, different question.
 */
/**
 * What `POST /api/properties/:id/valuations/indexed` answers with.
 *
 * ==========================================================================================
 * IT CARRIES THE ARITHMETIC, NOT JUST THE ANSWER
 * ==========================================================================================
 *
 * "Your house is now worth €512,350" is not a sentence to assert without showing where it came
 * from, so the anchor and both index readings travel with the stored entry. A member who can see
 * that it derives from their own March 2024 valuation and a regional move of 10.6% can judge
 * whether to believe it — the same honesty FR-031 puts on the page. Every field below is therefore
 * load-bearing rather than diagnostic, which is why the shape is worth pinning.
 *
 * ==========================================================================================
 * WHY THIS SCHEMA EXISTS AT ALL, AND THE RULE IT OBEYS
 * ==========================================================================================
 *
 * `ExpensesPageDtoSchema` was written, exported, and imported by nothing — so it drifted into
 * fiction while every test went on passing. 009 turned that into a rule: **a response DTO schema is
 * parsed against a real response in a test, or it is not written.** This one is parsed in
 * `indexed-valuation-contract.test.ts` against the route's own reply, so a field renamed in
 * `describe()` fails there rather than in a browser.
 *
 * `value` and the readings are STRINGS. The value is a decimal amount, which must not become a
 * float on the way through JSON; the readings are CBS's one-decimal figures rendered as CBS
 * publishes them, from the integer tenths they are held as.
 */
export const IndexedValuationDtoSchema = z.strictObject({
  valuationId: z.uuid(),
  /** Decimal, never a number — see the header. */
  value: z.string(),
  valuationDate: CalendarDateSchema,
  /**
   * The valuation this estimate was computed FROM — the newest `manual` or `woz` entry, never an
   * `index` one. Chaining estimates compounds rounding and makes a re-run square the growth, which
   * is why the source is narrowed to two values here rather than reusing the column's full enum.
   */
  anchor: z.strictObject({
    value: z.string(),
    valuationDate: CalendarDateSchema,
    source: z.enum(["manual", "woz"]),
  }),
  index: z.strictObject({
    /** The COROP region, trimmed — CBS pads it, to different widths per table. */
    region: z.string(),
    atAnchor: z.string(),
    now: z.string(),
  }),
  /** True when a row was appended (201); false when the figure was already on record (200). */
  created: z.boolean(),
});
export type IndexedValuationDto = z.infer<typeof IndexedValuationDtoSchema>;

export const PropertiesChartDtoSchema = z.object({
  range: PropertiesChartRangeSchema,
  windowStart: z.string().regex(/^\d{4}-\d{2}$/),
  windowEnd: z.string().regex(/^\d{4}-\d{2}$/),
  currentValue: z.array(ChartPointSchema),
  totalOwed: z.array(ChartPointSchema),
  equity: z.array(ChartPointSchema),
  gain: z.array(ChartPointSchema),
});
export type PropertiesChartDto = z.infer<typeof PropertiesChartDtoSchema>;
